/**
 * Login Page Component
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import '../styles/Auth.css';

const LOGIN_GUIDE_HIDE_KEY = 'wikigame:hide-login-guide-prompt';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [telegramLoading, setTelegramLoading] = useState(false);
  const [telegramMessage, setTelegramMessage] = useState('');
  const [showGuidePrompt, setShowGuidePrompt] = useState(true);
  const [dontShowGuideAgain, setDontShowGuideAgain] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from;
  const { user, login } = useAuth();

  // Save redirect location to localStorage if present in location.state
  useEffect(() => {
    const from = location.state?.from;
    if (from) {
      const dest = `${from.pathname}${from.search || ''}${from.hash || ''}`;
      try {
        localStorage.setItem('wikigame:redirect-after-login', dest);
      } catch (e) {
        console.error('Failed to save redirect destination:', e);
      }
    }
  }, [location.state]);

  // Redirect to original destination once logged in
  useEffect(() => {
    if (user) {
      const savedRedirect = localStorage.getItem('wikigame:redirect-after-login');
      if (savedRedirect) {
        localStorage.removeItem('wikigame:redirect-after-login');
        navigate(savedRedirect, { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [user, navigate]);

  useEffect(() => {
    document.body.classList.add('login-modal-open');
    const shouldHideGuidePrompt = localStorage.getItem(LOGIN_GUIDE_HIDE_KEY) === 'true';
    if (shouldHideGuidePrompt) {
      setShowGuidePrompt(false);
      setDontShowGuideAgain(true);
    }

    return () => {
      document.body.classList.remove('login-modal-open');
    };
  }, []);

  useEffect(() => {
    const telegram = window?.Telegram?.WebApp;
    if (!telegram) return;

    const initData = String(telegram.initData || '').trim();
    if (!initData) {
      setTelegramMessage('Open this app from Telegram to continue with Telegram login.');
      return;
    }

    let cancelled = false;

    const runTelegramAuth = async () => {
      setTelegramLoading(true);
      setTelegramMessage('Signing you in with Telegram...');
      try {
        telegram.ready();
        if (typeof telegram.expand === 'function') {
          telegram.expand();
        }
      } catch (_) {
        // Non-fatal; continue auth.
      }

      try {
        const response = await authAPI.telegramLogin(initData);
        if (cancelled) return;
        const { token, user: loggedInUser } = response.data;
        login(loggedInUser, token);
      } catch (err) {
        if (cancelled) return;
        const details = err?.response?.data?.error || err?.message || 'Telegram login failed';
        setTelegramMessage(details);
      } finally {
        if (!cancelled) {
          setTelegramLoading(false);
        }
      }
    };

    runTelegramAuth();

    return () => {
      cancelled = true;
    };
  }, [login, navigate]);

  const persistGuidePreference = () => {
    if (dontShowGuideAgain) {
      localStorage.setItem(LOGIN_GUIDE_HIDE_KEY, 'true');
    }
  };

  const handleOpenGuide = () => {
    persistGuidePreference();
    setShowGuidePrompt(false);
    navigate('/guide');
  };

  const handleDismissGuidePrompt = () => {
    persistGuidePreference();
    setShowGuidePrompt(false);
  };

  const closeLoginModal = () => {
    navigate('/');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authAPI.login(String(username).trim(), password);
      const { token, user: loggedInUser } = response.data;

      login(loggedInUser, token);
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;

      let details = '';
      if (data && typeof data === 'object' && data.error) {
        details = data.error;
      } else if (typeof data === 'string' && data.trim()) {
        details = data.slice(0, 200);
      } else if (data) {
        try {
          details = JSON.stringify(data);
        } catch (_) {
          details = String(data);
        }
      } else {
        details = err?.message || 'Login failed';
      }

      if (!status) {
        const attemptedBase = err?.config?.baseURL || window?.__WIKIGAME_API_URL || '';
        const attemptedPath = err?.config?.url || '/auth/login';
        const attemptedUrl = attemptedBase
          ? `${String(attemptedBase).replace(/\/+$/, '')}/${String(attemptedPath).replace(/^\/+/, '')}`
          : attemptedPath;
        details = `${details} (endpoint: ${attemptedUrl})`;
      }

      setError(`${status ? `HTTP ${status}: ` : ''}${details}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-modal-page">
      <div className="login-modal-overlay" role="presentation" onClick={closeLoginModal}>
        <div
          className="auth-card login-modal-card"
          role="dialog"
          aria-modal="true"
          aria-labelledby="login-title"
          onClick={(event) => event.stopPropagation()}
        >
          <button type="button" className="login-modal-close" onClick={closeLoginModal} aria-label="Close login">
            x
          </button>
        <h1>Wiki Game</h1>
        <h2 id="login-title">Login</h2>

        {showGuidePrompt && (
          <div className="guide-login-prompt" role="note" aria-live="polite">
            <p className="guide-login-prompt__text">Would you like to read the game guide before login?</p>
            <label className="guide-login-prompt__check">
              <input
                type="checkbox"
                checked={dontShowGuideAgain}
                onChange={(e) => setDontShowGuideAgain(e.target.checked)}
              />
              {/* <span>Don't show this again</span> */}
            </label>
            <div className="guide-login-prompt__actions">
              <button type="button" className="guide-btn guide-btn--primary" onClick={handleOpenGuide}>
                Read Guide
              </button>
              <button type="button" className="guide-btn guide-btn--ghost" onClick={handleDismissGuidePrompt}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <input
            type="text"
            placeholder="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            required
          />

          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />

          <button type="submit" disabled={loading}>
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        <p>
          <Link to="/forgot-password">Forgot password?</Link>
        </p>
        <p>
        Don't have an account? <Link to="/register" state={{ from }}> Register here</Link>
        </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
